import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, CameraOff, CloudUpload, Download, RefreshCw, ScanLine, ShieldCheck, ShieldX, WifiOff } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";
import { toast } from "sonner";
import { api } from "../api";

const READER_ID = "control-qr-reader";
const MANIFEST_KEY = "lk_manifest";
const QUEUE_KEY = "lk_scan_queue";
const CONFLICTS_KEY = "lk_scan_conflicts";

const load = (k, d) => { try { return JSON.parse(localStorage.getItem(k)) ?? d; } catch { return d; } };
const save = (k, v) => localStorage.setItem(k, JSON.stringify(v));

const STATES = {
    ok: { bg: "bg-emerald-600", icon: ShieldCheck, title: "VALID TICKET", sub: "ALLOW ENTRY" },
    "offline-ok": { bg: "bg-emerald-700", icon: ShieldCheck, title: "VALID TICKET", sub: "ALLOW ENTRY — saved offline, will sync" },
    already: { bg: "bg-red-600", icon: ShieldX, title: "TICKET ALREADY USED", sub: "DENY ENTRY" },
    cancelled: { bg: "bg-red-600", icon: ShieldX, title: "CANCELLED / REFUNDED", sub: "DENY ENTRY" },
    invalid: { bg: "bg-red-600", icon: ShieldX, title: "INVALID TICKET", sub: "DENY ENTRY" },
    unknown: { bg: "bg-amber-500", icon: WifiOff, title: "CANNOT VERIFY OFFLINE", sub: "Not on the downloaded list — DENY ENTRY" },
    "no-manifest": { bg: "bg-amber-500", icon: WifiOff, title: "NO OFFLINE LIST", sub: "Download the ticket list while online — DENY ENTRY for now" },
};

export const ControlScan = () => {
    const [online, setOnline] = useState(navigator.onLine);
    const [manifest, setManifest] = useState(() => load(MANIFEST_KEY, null));
    const [queue, setQueue] = useState(() => load(QUEUE_KEY, []));
    const [conflicts, setConflicts] = useState(() => load(CONFLICTS_KEY, []));
    const [result, setResult] = useState(null);
    const [code, setCode] = useState("");
    const [busy, setBusy] = useState(false);
    const [camOn, setCamOn] = useState(false);
    const [camError, setCamError] = useState("");
    const scannerRef = useRef(null);
    const resultTimer = useRef(null);

    const persistQueue = (q) => { setQueue(q); save(QUEUE_KEY, q); };
    const persistConflicts = (c) => { setConflicts(c); save(CONFLICTS_KEY, c); };

    const refreshManifest = useCallback(async () => {
        const { data } = await api.get("/control/ticket-manifest");
        setManifest(data);
        save(MANIFEST_KEY, data);
        return data;
    }, []);

    useEffect(() => {
        if (navigator.onLine && !manifest) refreshManifest().catch(() => {});
    }, []); // eslint-disable-line

    const syncQueue = useCallback(async (q) => {
        const pending = q ?? load(QUEUE_KEY, []);
        if (!navigator.onLine || !pending.length) return;
        try {
            const { data } = await api.post("/control/checkin/sync", { scans: pending });
            const done = new Map(data.results.map((r) => [r.code, r]));
            const still = pending.filter((s) => !done.has(s.code));
            const newConflicts = data.results.filter((r) => r.result !== "accepted");
            persistQueue(still);
            if (newConflicts.length) persistConflicts([...load(CONFLICTS_KEY, []), ...newConflicts]);
            if (data.results.some((r) => r.result === "accepted")) toast.success(`${data.results.filter((r) => r.result === "accepted").length} offline check-in(s) synced`);
            refreshManifest().catch(() => {});
        } catch { /* still offline — keep queue */ }
    }, [refreshManifest]);

    useEffect(() => {
        const on = () => { setOnline(true); syncQueue(); };
        const off = () => setOnline(false);
        window.addEventListener("online", on);
        window.addEventListener("offline", off);
        const t = setInterval(() => syncQueue(), 15000);
        return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); clearInterval(t); };
    }, [syncQueue]);

    const showResult = (r) => {
        clearTimeout(resultTimer.current);
        setResult(r);
        resultTimer.current = setTimeout(() => setResult(null), 4500);
    };

    const offlineCheck = (value) => {
        if (!manifest) return showResult({ kind: "no-manifest", code: value });
        const entry = manifest.tickets.find((t) => t.code === value);
        if (!entry) return showResult({ kind: "unknown", code: value });
        if (entry.status === "used" || entry.status === "used-local" || queue.some((s) => s.code === value)) {
            return showResult({ kind: "already", ticket: entry, code: value });
        }
        if (entry.status !== "valid") return showResult({ kind: "cancelled", ticket: entry, code: value });
        const scan = { code: value, scanned_at: new Date().toISOString() };
        persistQueue([...queue, scan]);
        entry.status = "used-local";
        save(MANIFEST_KEY, manifest);
        setManifest({ ...manifest });
        showResult({ kind: "offline-ok", ticket: entry, code: value });
    };

    const submitCode = async (raw) => {
        const value = (raw || "").trim().toUpperCase();
        if (!value || busy) return;
        setBusy(true);
        setCode("");
        if (!navigator.onLine) {
            offlineCheck(value);
            setBusy(false);
            return;
        }
        try {
            const { data } = await api.post("/checkin", { code: value });
            const map = { ok: "ok", already_used: "already", cancelled: "cancelled", invalid: "invalid" };
            showResult({ kind: map[data.result] || "invalid", ticket: data.ticket, code: value });
            if (data.result === "ok" && manifest) {
                const e = manifest.tickets.find((t) => t.code === value);
                if (e) { e.status = "used"; save(MANIFEST_KEY, manifest); setManifest({ ...manifest }); }
            }
        } catch (err) {
            if (!err.response) offlineCheck(value);   // network failed mid-scan → offline path
            else showResult({ kind: "invalid", code: value });
        } finally {
            setBusy(false);
        }
    };

    const startCamera = async () => {
        setCamError("");
        try {
            const scanner = new Html5Qrcode(READER_ID);
            scannerRef.current = scanner;
            await scanner.start({ facingMode: "environment" }, { fps: 10, qrbox: { width: 240, height: 240 } },
                (decoded) => { if (!busy) submitCode(decoded); }, () => {});
            setCamOn(true);
        } catch (e) {
            setCamError("Camera unavailable — use manual code entry below.");
        }
    };

    const stopCamera = async () => {
        try { if (scannerRef.current) { await scannerRef.current.stop(); scannerRef.current.clear(); } } catch {}
        scannerRef.current = null;
        setCamOn(false);
    };
    useEffect(() => () => { stopCamera(); }, []); // eslint-disable-line

    const st = result ? STATES[result.kind] : null;

    return (
        <div className="space-y-4" data-testid="control-scan">
            <div className="flex items-center justify-between">
                <h1 className="font-display font-black text-xl">Ticket Scanner</h1>
                <span data-testid="scan-pending"
                    className={`rounded-full px-3 py-1.5 font-mono2 text-[9px] uppercase tracking-[0.15em] border ${queue.length ? "border-amber-500/50 text-amber-400" : "border-lakou-gold/20 text-lakou-sand"}`}>
                    {queue.length} pending sync
                </span>
            </div>

            {/* offline manifest card */}
            <div className="rounded-2xl border border-lakou-gold/15 bg-lakou-coal/60 p-4 space-y-3" data-testid="manifest-card">
                <div className="flex items-center justify-between gap-3">
                    <div>
                        <div className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-lakou-gold">Offline ticket list</div>
                        <div className="text-xs text-lakou-grey mt-1" data-testid="manifest-status">
                            {manifest ? `${manifest.tickets.length} tickets · updated ${new Date(manifest.generated_at).toLocaleString()}` : "Not downloaded — required for offline scanning"}
                        </div>
                    </div>
                    <button onClick={() => refreshManifest().then(() => toast.success("Ticket list updated")).catch(() => toast.error("Download failed — check connection"))}
                        disabled={!online} data-testid="manifest-download"
                        className="inline-flex items-center gap-2 rounded-full bg-lakou-gold text-lakou-ink font-display font-bold text-[10px] uppercase tracking-[0.12em] px-4 py-2.5 disabled:opacity-40">
                        <Download size={12} /> {manifest ? "Refresh" : "Download"}
                    </button>
                </div>
                {queue.length > 0 && (
                    <button onClick={() => syncQueue()} disabled={!online} data-testid="sync-now"
                        className="inline-flex items-center gap-2 rounded-full border border-amber-500/50 text-amber-400 font-mono2 text-[10px] uppercase tracking-[0.12em] px-4 py-2 disabled:opacity-40">
                        <CloudUpload size={12} /> Sync {queue.length} offline check-in{queue.length > 1 ? "s" : ""} now
                    </button>
                )}
            </div>

            {/* conflicts from sync */}
            {conflicts.length > 0 && (
                <div className="rounded-2xl border border-red-500/40 bg-red-950/40 p-4 space-y-2" data-testid="sync-conflicts">
                    <div className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-red-400">Sync conflicts — review before admitting</div>
                    {conflicts.map((c, i) => (
                        <div key={`${c.code}-${i}`} className="flex items-center justify-between text-xs">
                            <span className="font-mono2 text-red-200">{c.code} — {c.result}{c.by ? ` (first scanned by ${c.by} at ${c.used_at?.slice(11, 19)})` : ""}</span>
                            <button onClick={() => persistConflicts(conflicts.filter((_, j) => j !== i))} data-testid={`conflict-dismiss-${i}`}
                                className="text-red-400 font-mono2 text-[9px] uppercase tracking-wider">Acknowledge</button>
                        </div>
                    ))}
                </div>
            )}

            {/* camera + manual */}
            <div className="rounded-2xl border border-lakou-gold/15 bg-lakou-coal/60 p-4 space-y-3">
                <div className="flex gap-2">
                    <button onClick={camOn ? stopCamera : startCamera} data-testid="scan-camera-toggle"
                        className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-lakou-orange hover:bg-lakou-gold text-white hover:text-lakou-ink font-display font-bold text-xs uppercase tracking-[0.15em] py-3.5 transition-colors">
                        {camOn ? <><CameraOff size={15} /> Stop camera</> : <><Camera size={15} /> Start camera scan</>}
                    </button>
                </div>
                <div id={READER_ID} className={`rounded-xl overflow-hidden ${camOn ? "" : "hidden"}`} data-testid="scan-reader" />
                {camError && <p className="text-xs text-amber-400" data-testid="scan-cam-error">{camError}</p>}
                <form onSubmit={(e) => { e.preventDefault(); submitCode(code); }} className="flex gap-2">
                    <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="LAKOU-XXXXXXXX" data-testid="scan-manual-code"
                        className="flex-1 rounded-xl border border-lakou-gold/20 bg-lakou-raise/70 px-4 py-3 font-mono2 text-sm tracking-[0.15em] text-lakou-cream placeholder:text-lakou-sand focus:outline-none focus:border-lakou-gold" />
                    <button type="submit" disabled={busy} data-testid="scan-submit"
                        className="rounded-xl bg-lakou-gold text-lakou-ink px-5 font-display font-bold text-xs uppercase tracking-wider inline-flex items-center gap-1.5 disabled:opacity-50">
                        <ScanLine size={14} /> Check
                    </button>
                </form>
            </div>

            {/* fullscreen result */}
            <AnimatePresence>
                {result && st && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                        className={`fixed inset-0 z-[60] ${st.bg} flex flex-col items-center justify-center text-center px-6`}
                        data-testid={`scan-result-${result.kind}`}
                        onClick={() => setResult(null)}
                    >
                        <st.icon size={90} className="text-white drop-shadow-lg" />
                        <div className="mt-6 font-display font-black text-3xl sm:text-4xl text-white tracking-tight">{st.title}</div>
                        <div className="mt-2 font-mono2 text-xs uppercase tracking-[0.3em] text-white/80">{st.sub}</div>
                        {result.ticket && (
                            <div className="mt-8 rounded-2xl bg-black/25 px-8 py-5 space-y-1.5">
                                <div className="font-display font-bold text-xl text-white">{result.ticket.attendee_name}</div>
                                <div className="font-mono2 text-sm text-white/90 tracking-[0.15em]">{result.code}</div>
                                <div className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-white/70">{result.ticket.category}</div>
                                {result.ticket.used_at && result.kind === "already" && (
                                    <div className="font-mono2 text-[10px] text-white/70">used {result.ticket.used_at.slice(11, 19)}{result.ticket.checked_in_by ? ` by ${result.ticket.checked_in_by}` : ""}</div>
                                )}
                            </div>
                        )}
                        <p className="mt-10 font-mono2 text-[10px] uppercase tracking-[0.25em] text-white/60">Tap anywhere to scan next</p>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};
